import * as React from 'react'
import { useSelector } from 'react-redux'
import { useFormRequest } from '@hooks/use-form-request'
import { commonObjectPost } from '@requests/basic-requests'
import { AppHeaderSection } from '@components/app-header-section'
import { FormProvider, useForm } from 'react-hook-form'
import { PaymentActionRow } from '@components/payments/payment-action-row'
import { AppFooter } from '@components/app-footer'
import { selectAppDataUrls } from '@store/selectors/app-data-selector'
import { usePaymentSelection } from '@hooks/use-payment-selection'
import { ProductKinds } from '@components/payments/payment-selection-view'
import { Agreements } from '@modules/package-wholesale/details/package-wholesale-agreements'
import { ResortImages } from '@modules/package-wholesale/details'
import { useScrollIntoView } from '@hooks/use-scroll-into-view'
import { useProductDetails } from '@hooks/use-product-details'
import { DynamicAgreementsSection } from '@components/agreement/dynamic-agreements-section'
import { BookingOptionsDetails } from '@modules/booking-options/models'
import { BookingOptionHeaderSectionContent } from '@modules/booking-options/details/booking-options-header-section-content'
import { BookingOptionContent } from '@modules/booking-options/details/booking-options-details-content'
import { SideCard } from '@components/side-card'

interface FormInputs extends Agreements {
  rules: string
}

export const BookingOptionsView = (): JSX.Element => {
  const paymentSectionRef = React.useRef<HTMLDivElement>(null)
  const { scrollTo } = useScrollIntoView()

  const { products } = useSelector(selectAppDataUrls)

  const {
    productDetails: bookingOptionsDetails,
    token,
    PageWrapper,
  } = useProductDetails<BookingOptionsDetails>(products.client_options_purchase)

  const methods = useForm<FormInputs>({ reValidateMode: 'onChange' })
  const summarySectionRef = React.useRef<HTMLDivElement>(null)

  const { showPaymentSelectionView } = usePaymentSelection()

  const { isLoading: isSaving, action: onSubmit } = useFormRequest(async payload => {
    if (!bookingOptionsDetails || !token) return

    await commonObjectPost(bookingOptionsDetails?.urls.rules, payload)

    showPaymentSelectionView(token, ProductKinds.BOOKING_OPTIONS)
  }, methods.setError)

  return (
    <PageWrapper>
      <AppHeaderSection images={ResortImages} imageDescription="Opcje do rezerwacji">
        <BookingOptionHeaderSectionContent onScrollToBill={scrollTo(summarySectionRef)} />
      </AppHeaderSection>
      <div className="container app-header-section__after-content">
        <div className="row gx-xl-5">
          <div className="card col-lg-9 col-12 py-4 card-shadow border-0 overflow-hidden" ref={summarySectionRef}>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                {bookingOptionsDetails && (
                  <BookingOptionContent
                    bookingOption={bookingOptionsDetails}
                    onScrollToPayment={scrollTo(paymentSectionRef)}
                  />
                )}
                <hr className="mx-n5" />
                {bookingOptionsDetails && !bookingOptionsDetails?.is_paid && (
                  <DynamicAgreementsSection
                    productRules={[...bookingOptionsDetails.required_rules, ...bookingOptionsDetails.optional_rules]}
                  />
                )}
                <PaymentActionRow
                  isPaymentDisabled={bookingOptionsDetails?.is_paid}
                  ref={paymentSectionRef}
                  isLoading={isSaving}
                  className="mt-4"
                  amount={bookingOptionsDetails?.is_paid ? '0' : bookingOptionsDetails?.price_brutto || '0'}
                />
              </form>
            </FormProvider>
          </div>
          <SideCard
            className="d-lg-block d-none"
            image={
              <img
                src={require('@assets/images/subscription-label.jpg')}
                alt="Dodatkowe opcje do rezerwacji w Holiday Park & Resort"
              />
            }
          />
        </div>
      </div>
      <AppFooter />
    </PageWrapper>
  )
}
